import React, { useState } from 'react';
import { DevFillButton, DEVFILL_TID } from 'src/devtools/DevFillButton';
import { DEVFILL_ENABLED } from 'src/devtools/gate';

import { PhqStepScaffold } from '../flow/PhqStepScaffold';
import { W2SelectionStepLayout } from '../selection/components/W2SelectionStepLayout';
import { W2_TID } from '../constants/testids';
import { W2_WAIVER_COPY, MEDICAL_WAIVE_CERTIFICATION } from '../copy';

type WaiverChoice = 'coverage' | 'waive';

interface W2WaiverModalProps {
  /** "I want coverage" → reveal the Welcome screen + the PHQ flow (spec screen 4). */
  onCoverage: () => void;
  /**
   * "I waive coverage" (+ certify) → persist the waiver and advance to the waiver
   * terminal screen (spec screen 3). Rejects on failure so the screen can surface
   * a retryable error without losing the employee's selection.
   */
  onWaive: (certificationText: string) => Promise<void>;
}

/**
 * Spec screen 2 — the initial waiver gate, the first PHQ screen an employee sees
 * (after W2 mode is active, so the isolation invariant holds — it never mounts
 * during `auth`/before activation). Re-homed from a centered modal overlay into
 * the W2-owned two-panel shell (`PhqStepScaffold` + `W2SelectionStepLayout`) so
 * the whole PHQ flow shares ONE design; the required-action gating is UNCHANGED.
 *
 * Following the `AgreementSignature` pattern, the verbatim headline/body sit in
 * the left maroon panel while the required action stays IN the middle card: the
 * panel shows no NEXT (`hideNext`) and the in-card Submit is the only advance.
 * An answer is REQUIRED — Submit is inactive (the exact light gray `#ececec`)
 * until a valid choice. Choosing "I waive coverage" reveals the certification
 * checkbox, and Submit activates only once BOTH waive AND certify are selected.
 */
export const W2WaiverModal: React.FC<W2WaiverModalProps> = ({
  onCoverage,
  onWaive,
}) => {
  const [choice, setChoice] = useState<WaiverChoice | null>(null);
  const [certified, setCertified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const complete = choice === 'coverage' || (choice === 'waive' && certified);
  const submitEnabled = complete && !submitting;

  const handleSubmit = async () => {
    if (!submitEnabled) return;
    if (choice === 'coverage') {
      onCoverage();
      return;
    }
    // choice === 'waive'
    setSubmitting(true);
    setError(null);
    try {
      await onWaive(MEDICAL_WAIVE_CERTIFICATION);
    } catch {
      setError('We could not record your waiver. Please try again.');
      setSubmitting(false);
    }
  };

  const selectChoice = (next: WaiverChoice) => {
    setChoice(next);
    // Leaving the waive option clears the certification so Submit can't stay
    // enabled on a stale check.
    if (next !== 'waive') setCertified(false);
  };

  return (
    <PhqStepScaffold>
      {DEVFILL_ENABLED && (
        <DevFillButton
          testid={DEVFILL_TID.waiver}
          label="Fill waive"
          position="bottom-left"
          onFill={() => {
            selectChoice('waive');
            setCertified(true);
          }}
        />
      )}
      <W2SelectionStepLayout
        step={1}
        total={1}
        hideStepLabel
        hideNext
        title={W2_WAIVER_COPY.headline}
        body={<p>{W2_WAIVER_COPY.body}</p>}
        rootTestId={W2_TID.waiver.root}
      >
        <div className="flex flex-col">
          <fieldset className="flex flex-col gap-3">
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#ececf2] p-4 text-[15px] text-[#1a1a2e] has-[:checked]:border-[#1a1a2e]">
              <input
                type="radio"
                name="w2-waiver-choice"
                data-testid={W2_TID.waiver.coverage}
                checked={choice === 'coverage'}
                onChange={() => selectChoice('coverage')}
                className="mt-1 accent-primary"
              />
              <span>{W2_WAIVER_COPY.coverageLabel}</span>
            </label>

            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#ececf2] p-4 text-[15px] text-[#1a1a2e] has-[:checked]:border-[#1a1a2e]">
              <input
                type="radio"
                name="w2-waiver-choice"
                data-testid={W2_TID.waiver.waive}
                checked={choice === 'waive'}
                onChange={() => selectChoice('waive')}
                className="mt-1 accent-primary"
              />
              <span>{W2_WAIVER_COPY.waiveLabel}</span>
            </label>
          </fieldset>

          {choice === 'waive' && (
            <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl bg-[#f6f7fb] p-4 text-[13px] leading-5 text-[#5a5a70]">
              <input
                type="checkbox"
                data-testid={W2_TID.waiver.certify}
                checked={certified}
                onChange={(e) => setCertified(e.target.checked)}
                className="mt-1 shrink-0 accent-primary"
              />
              <span>{MEDICAL_WAIVE_CERTIFICATION}</span>
            </label>
          )}

          {error && (
            <p
              data-testid={W2_TID.waiver.error}
              className="mt-4 text-sm text-error"
            >
              {error}
            </p>
          )}

          <button
            type="button"
            data-testid={W2_TID.waiver.submit}
            disabled={!submitEnabled}
            onClick={handleSubmit}
            className="mt-6 w-full rounded-full px-6 py-3 text-[15px] font-semibold transition-colors"
            // Enabled Submit is the brand colour via the design token
            // (`--color-primary`, #710036) — not a hardcoded navy. Spec:
            // disabled Submit is the exact light gray #ececec.
            style={
              submitEnabled
                ? {
                    backgroundColor: 'var(--color-primary)',
                    color: '#ffffff',
                  }
                : {
                    backgroundColor: '#ececec',
                    color: '#9a9aa5',
                    cursor: 'not-allowed',
                  }
            }
          >
            {submitting ? 'Submitting…' : W2_WAIVER_COPY.submitLabel}
          </button>
        </div>
      </W2SelectionStepLayout>
    </PhqStepScaffold>
  );
};

export default W2WaiverModal;
